import { Card, CardBody, CardHeader, CardTitle, Progress } from '@vnetwork/ui/primitives';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import type { ProgressTone } from '@vnetwork/ui/primitives';
import type { CustomerUptimeStats } from '@/api/customer-sla';
import { formatDuration } from './format-duration';

/** Uptime % → tone: ≥99.5 success, ≥99 neutral, else danger (legacy thresholds). */
function uptimeTone(pct: number): ProgressTone {
  if (pct >= 99.5) return 'success';
  if (pct >= 99) return 'neutral';
  return 'danger';
}

const UPTIME_COLOR: Record<ProgressTone, string> = {
  success: 'var(--vn-success-text)',
  neutral: 'var(--vn-fg-default)',
  danger: 'var(--vn-danger-text)',
  primary: 'var(--vn-primary-text)',
  warning: 'var(--vn-warning-text)',
  info: 'var(--vn-info-text)',
};

/**
 * Section 1 — customer uptime overview for the current period. SLA count, average
 * uptime (with a Progress bar), services meeting SLA, downtime and incidents.
 * Mirrors the legacy ManageSLA "Tổng quan Uptime" band; period is fixed to the
 * current month (a date-range filter is deferrable).
 */
export function UptimeOverview({ stats }: { stats: CustomerUptimeStats }) {
  const tone = uptimeTone(stats.avg_uptime_percent);
  const breaching = stats.services_breaching_sla > 0;
  const noDowntime = stats.total_sla_downtime_minutes <= 0;

  return (
    <Card>
      <CardHeader
        actions={
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            This month
          </span>
        }
      >
        <CardTitle>Uptime Overview</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <StatGroup columns={5}>
          <Stat label="SLA services" value={stats.total_services} />
          <Stat
            label="Uptime"
            value={
              <span style={{ color: UPTIME_COLOR[tone] }}>{stats.avg_uptime_percent.toFixed(2)}%</span>
            }
          />
          <Stat label="Met" value={`${stats.services_meeting_sla}/${stats.total_services}`} />
          <Stat
            label="Downtime"
            value={formatDuration(stats.total_sla_downtime_minutes)}
            hint={breaching ? `${stats.services_breaching_sla} breaching` : undefined}
            trend={breaching ? 'down' : undefined}
          />
          <Stat label="Incidents" value={stats.total_incidents} />
        </StatGroup>
        <Progress value={stats.avg_uptime_percent} max={100} tone={tone} size="md" showValue />
        {noDowntime ? (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-success-text)' }}>
            No downtime in this period
          </span>
        ) : null}
      </CardBody>
    </Card>
  );
}
